import { AccountSelector, AnimatedContentLoader, Button, Input, Switch } from '@components'
import { useBusinessContext } from '@context'
import { useQueryParam, useTheme } from '@hooks'
import {
  SlideContent,
  SlideFooter,
  SlideText,
  SlideTitle,
  Spacer
} from '@pages/BankIntegrationOnboardingPage/slides/slide.styled'
import {
  deleteBankIntegration,
  fetchBankIntegration,
  EnableBankingAccountConnectionBlock,
  IBankIntegration,
  invalidateFirstTransactionFetchNotice,
  updateBankIntegration
} from '@query'
import {
  AccountType,
  ENABLEBANKING_CASH_ACCOUNT_TYPE_OPTIONS,
  EnablebankingCashAccountType
} from '@root/constants'
import { findOptionByValue } from '@root/utils'
import { setAPIErrors, type APIError } from '@utils'
import { tryFormatIBAN } from '@utils/iban'
import dayjs from 'dayjs'
import React, { useCallback } from 'react'
import { Controller, get, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowsAltH, FaCheck } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import toast from 'react-hot-toast'

interface Props {
  isEdit: boolean
  goBack: () => void
  onDone: () => void
}

const FINALIZE_FORM_ID = 'finalize-form'

export const FinalizeSlide: React.FC<Props> = ({ isEdit, onDone, goBack }) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [integrationId] = useQueryParam('id')
  const theme = useTheme()

  const {
    data: integration,
    isLoading: isQueryLoading,
    isPending,
    refetch
  } = useQuery({
    queryKey: [slug, 'bank_integration', integrationId],
    queryFn: () => fetchBankIntegration({ slug, integrationId }),
    ...{
      enabled: !!integrationId
    }
  })

  const { mutateAsync: updateIntegration } = useMutation<
    unknown,
    unknown,
    Partial<IBankIntegration>
  >({
    mutationFn: data => updateBankIntegration({ slug, integrationId: integration.id }, data)
  })

  const { mutateAsync: deleteIntegration, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteBankIntegration({ slug, integrationId: integration.id })
  })

  const handleGoBack = useCallback(async () => {
    if (!isEdit) {
      await deleteIntegration()
    }
    goBack()
  }, [])

  const {
    handleSubmit,
    register,
    control,
    setError,
    formState: { errors, isLoading: isFormLoading }
  } = useForm<IBankIntegration>({
    defaultValues: async () => {
      const { data: integration } = await refetch()
      return {
        ...integration,
        accounts: integration.accounts.some(({ enabled }) => enabled)
          ? // CASE: At least one account is enabled
            // > Keep accounts as they are
            integration.accounts
          : // CASE: No account is enabled
            // > Enable the first account that can be connected
            integration.accounts.map((account, index) => ({
              ...account,
              enabled:
                index ===
                integration.accounts.findIndex(
                  candidate =>
                    candidate.connection_block !== EnableBankingAccountConnectionBlock.NON_EUR
                )
            }))
      }
    }
  })
  const { fields: accountFields } = useFieldArray({
    control,
    name: 'accounts',
    keyName: '__id'
  })

  const maxHistoricalDate = dayjs().subtract(integration?.max_historical_days, 'day')
  const maxHistoricalDateISO = maxHistoricalDate.format('YYYY-MM-DD')

  const onSubmit = handleSubmit(async data => {
    try {
      await updateIntegration({
        ...data,
        enabled: true // Mark integration as enabled
      })
      await invalidateFirstTransactionFetchNotice(queryClient, slug)
      onDone()
    } catch (err: unknown) {
      if (
        typeof err === 'object' &&
        err !== null &&
        'status' in err &&
        err.status === 400 &&
        'data' in err
      ) {
        setAPIErrors(err.data as APIError, setError)
        return
      }

      toast.error(t('general.error'))
    }
  })

  const watchedAccounts = useWatch({
    control,
    name: 'accounts'
  })

  const showSpinner = isQueryLoading || isPending || isFormLoading
  const allAccountsBlocked =
    accountFields.length > 0 &&
    accountFields.every(
      account => account.connection_block === EnableBankingAccountConnectionBlock.NON_EUR
    )
  const cannotFinish = allAccountsBlocked && !watchedAccounts?.some(account => account.enabled)

  const getAccountTitle = (account: IBankIntegration['accounts'][0]): string => {
    if (account?.details) {
      return account.details
    }
    const labelKey = findOptionByValue(
      ENABLEBANKING_CASH_ACCOUNT_TYPE_OPTIONS,
      account.cash_account_type
    )?.labelKey

    return labelKey
      ? t(labelKey)
      : t(
          findOptionByValue(
            ENABLEBANKING_CASH_ACCOUNT_TYPE_OPTIONS,
            EnablebankingCashAccountType.CACC
          ).labelKey
        )
  }

  return (
    <>
      <SlideContent>
        <StyledForm id={FINALIZE_FORM_ID} onSubmit={onSubmit}>
          <AnimatedContentLoader isLoading={showSpinner}>
            {isEdit ? (
              <SlideTitle>
                {t('bankOnboarding.finalize.editTitle', { bankName: integration?.name })}
              </SlideTitle>
            ) : (
              <SlideTitle>
                {t('bankOnboarding.finalize.createTitle', { bankName: integration?.name })}
              </SlideTitle>
            )}
            <SlideText>{t('bankOnboarding.finalize.description')}</SlideText>
            <LogoWrapper>
              <LogoImg $src={integration?.logo} className="left" />
              <FaArrowsAltH />
              <LogoImg $src={theme.assets.logo} className="right" />
            </LogoWrapper>

            <Spacer />

            {accountFields.map((account, index) => {
              const accountBlocked =
                account.connection_block === EnableBankingAccountConnectionBlock.NON_EUR
              return (
                <StyledAccountWrapper key={`account-${account.id}`} $blocked={accountBlocked}>
                  <StyledAccountHeaderWrapper>
                    <label htmlFor={`accounts-${index}-enabled-switch`}>
                      <StyledBusinessName>{account?.name}</StyledBusinessName>
                      <StyledAccountTitle>{getAccountTitle(account)}</StyledAccountTitle>
                      <StyledAccountIdentifier>
                        {tryFormatIBAN(account.identifier)}
                      </StyledAccountIdentifier>
                      {accountBlocked && (
                        <StyledBlockedNote>
                          {t('bankOnboarding.finalize.eurOnly')}
                        </StyledBlockedNote>
                      )}
                    </label>

                    <Controller
                      control={control}
                      name={`accounts.${index}.enabled`}
                      render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                        <Switch
                          label=""
                          height={24}
                          id={`accounts-${index}-enabled-switch`}
                          ref={ref}
                          checked={value}
                          disabled={accountBlocked && !value}
                          onChange={event => {
                            if (accountBlocked && event.target.checked) {
                              return
                            }
                            onChange(event)
                          }}
                          error={error}
                        />
                      )}
                    />
                  </StyledAccountHeaderWrapper>

                  {watchedAccounts?.[index]?.enabled && (
                    <>
                      <Controller
                        control={control}
                        name={`accounts.${index}.account_id`}
                        rules={{
                          required: { value: true, message: t('validation.required') }
                        }}
                        render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                          <>
                            <AccountSelector
                              id={`accounts-${index}-account-select`}
                              label={t('bankOnboarding.finalize.accountLabel')}
                              ref={ref}
                              error={error}
                              value={value}
                              onChange={onChange}
                              required={true}
                              errors={get(errors, `accounts.${index}.account_id`)}
                              accountFilter={{ type: AccountType.ASS_PAY }}
                              allowCreate={true}
                            />
                          </>
                        )}
                      />

                      <Input
                        type="date"
                        label={t('settings.integrations.bank.minSyncDate')}
                        info={t('settings.integrations.bank.minSyncDateInfo', {
                          maxHistoricalDays: integration?.max_historical_days
                        })}
                        required={true}
                        min={maxHistoricalDateISO}
                        errors={get(errors, `accounts.${index}.begin_sync_from`)}
                        {...register(`accounts.${index}.begin_sync_from`, {
                          required: { value: true, message: t('validation.required') }
                        })}
                        data-test="integration-date-input"
                      />
                    </>
                  )}
                </StyledAccountWrapper>
              )
            })}
            <Spacer />
          </AnimatedContentLoader>
        </StyledForm>
      </SlideContent>
      <SlideFooter>
        <Button
          type="button"
          icon={<FaArrowLeft />}
          onClick={() => handleGoBack()}
          showSpinner={isDeleting}
          disabled={isDeleting}
        >
          {t('general.back')}
        </Button>
        <Spacer />
        <Button
          type="submit"
          intent="success"
          icon={<FaCheck />}
          form={FINALIZE_FORM_ID}
          disabled={showSpinner || cannotFinish}
        >
          {isEdit ? t('general.save') : t('bankOnboarding.finalize.activate')}
        </Button>
      </SlideFooter>
    </>
  )
}

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const StyledAccountWrapper = styled.div<{ $blocked?: boolean }>`
  display: flex;
  flex-direction: column;
  border: 3px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  overflow: hidden;
  background: ${({ $blocked, theme }) =>
    $blocked ? theme.colors.neutralGray : theme.colors.neutralWhite};

  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;

  .input-wrapper {
    margin: 0;

    input {
      height: 1.6rem;
    }
  }
`

const StyledAccountHeaderWrapper = styled.div`
  display: flex;
  width: 100%;

  label {
    width: 100%;
    flex: 1;
    margin: 0;
  }

  .switch-wrapper {
    margin: 0;
  }
`

const StyledBusinessName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const StyledAccountTitle = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
`

const StyledAccountIdentifier = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const StyledBlockedNote = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
`

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  margin: ${({ theme }) => theme.spacing.xxl}rem 0;

  svg {
    flex: 1;
    height: clamp(1rem, 3vw, 1.25rem);
    fill: ${({ theme }) => theme.colors.metalGray};
  }
`

const LogoImg = styled.div<{ $src: string }>`
  flex: 2;
  height: clamp(32px, 8vw, 44px);
  background-image: ${({ $src }) => `url(${$src})`};
  background-size: contain;
  background-repeat: no-repeat;

  &.left {
    background-position: right center;
  }

  &.right {
    background-position: left center;
    height: clamp(22px, 5.5vw, 30px);
  }
`
